import * as React from 'react';

import type { ApolloError } from '@apollo/client';
import { Alert, AlertTitle, Box, Typography } from '@mui/material';

/**
 * Distinguishes "the API does not know this field" from "there is no data yet".
 * Those need completely different actions, and reporting the first as the
 * second sends people looking for a missing model that is actually there.
 *
 * PostGraphile derives its schema from the database and only exposes tables the
 * connecting role can SELECT, so a missing table or a missing GRANT both show
 * up as "Cannot query field ... on type Query". So does a client asking for a
 * field that was renamed — which looks identical and is far more common, so it
 * is named first.
 */

/** True when the error is the schema not knowing a field, not a data problem. */
export const isSchemaError = (error?: ApolloError): boolean =>
  !!error?.graphQLErrors?.some((e) => /Cannot query field/i.test(e.message));

const PerceptionModelError: React.FC<{ error: ApolloError }> = ({ error }) => {
  if (!isSchemaError(error)) {
    return <Alert severity="error">{error.message}</Alert>;
  }

  return (
    <Alert severity="error">
      <AlertTitle>The API does not recognise this field</AlertTitle>
      <Typography variant="body2" gutterBottom>
        This is a setup or naming problem, not missing data. Work through it in
        this order — the server's own suggestion at the bottom usually settles
        it in one step.
      </Typography>
      <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
        <li>
          <Typography variant="body2">
            Check the field name against the live schema first. If the server
            suggests a near-match, a table was renamed and the console is asking
            for the old name — a code fix, not a migration.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Otherwise confirm the <code>perception_model_*</code> tables exist
            and that the API role can <code>SELECT</code> them. PostGraphile
            hides tables it cannot read, so a missing grant looks exactly like a
            missing table.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            Reload the GraphQL server so it re-introspects. It will not pick up
            new tables on its own unless it runs in watch mode.
          </Typography>
        </li>
      </Box>
      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 1, color: 'text.secondary' }}
      >
        {error.message}
      </Typography>
    </Alert>
  );
};

export default PerceptionModelError;
